<script setup>
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useEventListener } from '@/directive/use-event-listener';

const modalPageConfig = {
  'name-edit': {
    label: 'edit name'
  },
  'email-edit': {
    label: 'edit email'
  },
  members: {
    label: 'Members'
  },
  managers: {
    label: 'Warranter'
  }
};

const dialogContent = ref(null);
const route = useRoute();
const router = useRouter();

const modalConfig = computed(() => {
  return modalPageConfig[route.query.modal] || {};
});
const modalName = computed(() => {
  return modalConfig.value.label;
});
const modalSize = computed(() => {
  return route.query.modalSize || 500;
});
const noScroll = computed(() => {
  return route.query.noScroll;
});

const showModal = computed(() => {
  return !!route.query.modal;
});

const closeModal = () => {
  if (!route.query.modal) return;
  router.replace({});
};

const firstElement = ref(null);
const lastElement = ref(null);
watch(
  () => route.query.modal,
  (newVal) => {
    if (!newVal) return;
    const focusableElements = dialogContent.value.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    firstElement.value = focusableElements[0];
    lastElement.value = focusableElements[focusableElements.length - 1];
  }
);

// trap focus to modal
useEventListener(window, 'keydown', (e) => {
  if (!showModal.value) return;
  if (e.key !== 'Tab') return;
  if (e.shiftKey && document.activeElement === firstElement.value) {
    e.preventDefault();
    lastElement.value.focus();
  } else if (!e.shiftKey && document.activeElement === lastElement.value) {
    e.preventDefault();
    firstElement.value.focus();
  }
});
</script>

<template>
  <transition name="fadeFast">
    <div
      v-show="showModal"
      class="v-modal desktop"
      :aria-labelledby="modalName"
      @click.self="closeModal"
      @keydown.esc.exact="closeModal"
      :class="{ scroll: !noScroll }"
    >
      <div
        ref="dialogContent"
        id="modal-content"
        class="content"
        role="dialog"
        :style="{ maxWidth: `${modalSize}px` }"
      ></div>
    </div>
  </transition>
</template>

<style lang="scss" scoped>
.modal-wrapper {
  overflow: hidden;
}

.v-modal {
  position: fixed;
  backdrop-filter: blur(4px);
  background-color: color-mix(
    in srgb,
    var(--color-bg-level-04),
    transparent 50%
  );
  z-index: 1;
  inset: 0;
  border: none;
  background-color: transparent;
  color: inherit;
  padding: var(--spacing-medium);
  .content {
    position: relative;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    height: auto;
    min-height: 150px;
    max-height: 90%;
    border-radius: var(--radius-small);
    background-color: var(--color-bg-level-01);
    will-change: transform;
  }
}

.modalSlide-enter-active {
  transition: all 0.35s ease;
}

.modalSlide-leave-active {
  transition: all 0.2s ease;
}

.modalSlide-enter,
.modalSlide-leave-to {
  opacity: 1;
  transform: translate3d(100%, 0, 0);
}

.modalSlide-enter-to,
.modalSlide-leave {
  opacity: 1;
  transform: translate3d(0, 0, 0);
}
</style>
